/**
 * Email Type and Reference Processing Utilities
 *
 * This module contains utility functions for determining email types,
 * managing email references, and processing email metadata.
 */

import type { MessageInfo } from '../../../api/cmail/cmail.dto';

export type EmailType = 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP';

export interface EmailMetadata {
  emailType: EmailType;
  references: string[];
  replyToMsgId?: string;
}

/**
 * Normalizes email type to uppercase for consistent comparison
 * @param emailType - Email type string that may be mixed-case
 * @returns Normalized uppercase email type
 */
const normalizeEmailType = (emailType?: string): EmailType => {
  if (!emailType) {
    return 'NEW';
  }

  const normalized = emailType.toUpperCase();

  // Validate that the normalized type is a valid EmailType
  if (['NEW', 'REPLY', 'FORWARD', 'FOLLOWUP'].includes(normalized)) {
    return normalized as EmailType;
  }

  return 'NEW';
};

/**
 * Determines email type based on mode and message metadata
 * @param mode - Email mode (new, reply, forward)
 * @param message - Message object containing metadata
 * @returns Email type for the current composition
 */
export const getEmailType = (
  mode: 'new' | 'reply' | 'reply-single' | 'forward',
  message?: MessageInfo
): 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP' => {
  // For new emails, return 'NEW'
  if (!message) {
    return 'NEW';
  }

  // Check if this is a draft that was originally a reply/forward
  const isDraft = message.emailDirection === 'DRAFT';
  const draftEmailType = normalizeEmailType(message.emailType);

  // For drafts, use the original email type from the message
  if (isDraft && draftEmailType) {
    if (draftEmailType === 'REPLY') {
      return 'REPLY'; // Always return uppercase for hooks
    }
    if (draftEmailType === 'FORWARD') {
      return 'FORWARD'; // Always return uppercase for hooks
    }
    return 'NEW';
  }

  // For non-draft messages, use the mode parameter
  if (mode === 'reply' || mode === 'reply-single') {
    return 'REPLY';
  }

  if (mode === 'forward') {
    return 'FORWARD';
  }

  return 'NEW';
};

/**
 * Ensures references array has unique values
 * @param references - Array of reference strings
 * @returns Array with duplicate references removed
 */
export const ensureUniqueReferences = (references: string[]): string[] => {
  return references.filter(
    (reference, index) => references.indexOf(reference) === index
  );
};

/**
 * Constructs references array for reply/forward emails
 * @param message - Original message object
 * @param mode - Email mode
 * @returns Array of reference message IDs
 */
export const getReferences = (
  message: MessageInfo,
  mode: 'new' | 'reply' | 'reply-single' | 'forward'
): string[] => {
  // For new emails, return empty array
  if (!message) {
    return [];
  }

  // Check if this is a draft that was originally a reply/forward
  const isDraft = message.emailDirection === 'DRAFT';
  const draftEmailType = normalizeEmailType(message.emailType);

  if (
    message &&
    (mode === 'reply' ||
      mode === 'reply-single' ||
      mode === 'forward' ||
      (isDraft && (draftEmailType === 'REPLY' || draftEmailType === 'FORWARD')))
  ) {
    // Use existing references from the message object if available
    let existingReferences: string[] = [];
    if (message.references && Array.isArray(message.references)) {
      existingReferences = message.references;
    }

    // Add the current message ID to the references only if it's not already there
    // This ensures unique references and prevents duplication
    if (!existingReferences.includes(message.msgId)) {
      const result = ensureUniqueReferences([
        ...existingReferences,
        message.msgId,
      ]);
      return result;
    }

    const result = ensureUniqueReferences(existingReferences);
    return result;
  }

  return [];
};

/**
 * Reply-to / In-Reply-To target for draft and send payloads.
 *
 * - Replying to a **non-draft** message: always `message.msgId` (the message the user
 *   is replying to). Do not use `message.replyToMsgId` on that row — it may point up-thread.
 * - **Draft** rows that are a reply/forward: use the draft's stored `replyToMsgId` (the
 *   parent in the thread); the draft's own `msgId` is the draft record, not the parent.
 */
export const getReplyToMsgId = (
  message: MessageInfo,
  mode: 'new' | 'reply' | 'reply-single' | 'forward'
): string | undefined => {
  if (!message) {
    return undefined;
  }

  const isDraft = message.emailDirection === 'DRAFT';
  const draftEmailType = normalizeEmailType(message.emailType);

  if (isDraft && (draftEmailType === 'REPLY' || draftEmailType === 'FORWARD')) {
    return message.replyToMsgId || message.msgId;
  }

  if (mode === 'reply' || mode === 'reply-single' || mode === 'forward') {
    return message.msgId;
  }

  return undefined;
};

/**
 * Message id for saveDraft and other draft flows. For reply/forward, only the
 * draft row id must be sent—omit when no draft exists so the API creates a new draft
 * instead of updating the original message.
 */
export const getMessageId = (
  message: MessageInfo,
  mode: 'new' | 'reply' | 'reply-single' | 'forward',
  currentDraftMsgId?: string | null
): string | undefined => {
  if (mode === 'reply' || mode === 'reply-single' || mode === 'forward') {
    return currentDraftMsgId || undefined;
  }

  return currentDraftMsgId || message?.msgId;
};

/**
 * Message id for POST /api/docmessage/v1/send only. Falls back to the original
 * message id when there is no draft yet so the backend can resolve attachments.
 * Do not use for saveDraft—use {@link getMessageId}.
 */
export const getMessageIdForSend = (
  message: MessageInfo | undefined,
  currentDraftMsgId?: string | null
): string | undefined => {
  return currentDraftMsgId || message?.msgId;
};

/**
 * Checks if a message is a draft
 * @param message - Message object to check
 * @returns True if message is a draft, false otherwise
 */
export const isDraftMessage = (message: MessageInfo): boolean => {
  return message?.emailDirection === 'DRAFT';
};

/**
 * Gets the subject prefix based on email mode
 * @param mode - Email mode
 * @param originalSubject - Original subject to prefix
 * @returns Subject with appropriate prefix
 */
export const getSubjectWithPrefix = (
  mode: 'new' | 'reply' | 'reply-single' | 'forward',
  originalSubject: string
): string => {
  switch (mode) {
    case 'reply':
    case 'reply-single':
      return `Re: ${originalSubject}`;
    case 'forward':
      return `Fwd: ${originalSubject}`;
    default:
      return originalSubject;
  }
};

/**
 * Extracts email metadata for API payload construction
 * @param message - Original message object
 * @param mode - Email mode
 * @returns Email metadata object
 */
export const extractEmailMetadata = (
  message: MessageInfo,
  mode: 'new' | 'reply' | 'reply-single' | 'forward'
): EmailMetadata => {
  const emailType = getEmailType(mode, message);
  const references = getReferences(message, mode);
  const replyToMsgId = getReplyToMsgId(message, mode);

  return {
    emailType,
    references,
    replyToMsgId,
  };
};

/**
 * Validates if message category should be included in API calls
 * @param msgCategory - Message category string
 * @returns True if category should be included, false otherwise
 */
export const shouldIncludeMessageCategory = (msgCategory?: string): boolean => {
  return !!msgCategory && msgCategory !== 'OTHER' && msgCategory !== 'Other';
};

/**
 * Normalizes a message category, in any of the casings it is written in, to
 * the backend enum value. Unknown values fall back to upper-casing.
 * @param msgCategory - Message category string
 * @returns Backend enum message category string
 *
 * @example
 * normalizeMessageCategoryForAPI('Lossruns') // returns 'LOSSRUNS'
 * normalizeMessageCategoryForAPI('lossruns') // returns 'LOSSRUNS'
 * normalizeMessageCategoryForAPI('LOSSRUNS') // returns 'LOSSRUNS'
 * normalizeMessageCategoryForAPI('Claimsops') // returns 'CLAIMS_OPS'
 * normalizeMessageCategoryForAPI('Submission') // returns 'SUBMISSION'
 * normalizeMessageCategoryForAPI('BOR') // returns 'BOR'
 * normalizeMessageCategoryForAPI('Other') // returns 'OTHER'
 * normalizeMessageCategoryForAPI(undefined) // returns 'SUBMISSION'
 */
export const normalizeMessageCategoryForAPI = (
  msgCategory?: string
): string => {
  if (!msgCategory) {
    return 'SUBMISSION';
  }

  // Handle known case variations - return uppercase for API
  const categoryMap: Record<string, string> = {
    Lossruns: 'LOSSRUNS',
    lossruns: 'LOSSRUNS',
    LOSSRUNS: 'LOSSRUNS',
    Claims: 'CLAIMS',
    claims: 'CLAIMS',
    CLAIMS: 'CLAIMS',
    ClaimsOps: 'CLAIMS_OPS',
    Claimsops: 'CLAIMS_OPS',
    claimsops: 'CLAIMS_OPS',
    CLAIMSOPS: 'CLAIMS_OPS',
    CLAIMS_OPS: 'CLAIMS_OPS',
    Submission: 'SUBMISSION',
    submission: 'SUBMISSION',
    SUBMISSION: 'SUBMISSION',
    BOR: 'BOR',
    bor: 'BOR',
    Binders: 'BINDERS',
    binders: 'BINDERS',
    BINDERS: 'BINDERS',
    CRS: 'CRS',
    crs: 'CRS',
    Subjectivity: 'SUBJECTIVITY',
    subjectivity: 'SUBJECTIVITY',
    SUBJECTIVITY: 'SUBJECTIVITY',
    Spam: 'SPAM',
    spam: 'SPAM',
    SPAM: 'SPAM',
    MTE: 'MTE',
    mte: 'MTE',
    Renewal: 'RENEWALS',
    renewal: 'RENEWALS',
    RENEWAL: 'RENEWALS',
    RENEWALS: 'RENEWALS',
    Cancellation: 'CANCELLATION',
    cancellation: 'CANCELLATION',
    CANCELLATION: 'CANCELLATION',
    CertRequest: 'CERT_REQUEST',
    certrequest: 'CERT_REQUEST',
    CERT_REQUEST: 'CERT_REQUEST',
    API: 'API',
    api: 'API',
    General: 'GENERAL',
    general: 'GENERAL',
    GENERAL: 'GENERAL',
    RiskEng: 'RISKENG',
    riskeng: 'RISKENG',
    RISKENG: 'RISKENG',
    'Risk Engineering': 'RISKENG',
    Finance: 'FINANCE',
    finance: 'FINANCE',
    FINANCE: 'FINANCE',
    Compliance: 'COMPLIANCE',
    compliance: 'COMPLIANCE',
    COMPLIANCE: 'COMPLIANCE',
    Other: 'OTHER',
    other: 'OTHER',
    OTHER: 'OTHER',
  };

  return categoryMap[msgCategory] || msgCategory.toUpperCase();
};

/**
 * Normalizes a message category, in any of the casings it is written in, to
 * the value the UI displays and filters on. Unknown values are returned
 * unchanged. Every casing of a category collapses to one value here, so a
 * category has exactly one UI spelling however it was stored — 'Claimsops',
 * 'claimsops', 'CLAIMSOPS' and 'CLAIMS_OPS' all read as 'ClaimsOps'.
 * @param msgCategory - Message category string
 * @returns UI message category string
 *
 * @example
 * normalizeMessageCategoryForUI('Lossruns') // returns 'LossRuns'
 * normalizeMessageCategoryForUI('lossruns') // returns 'LossRuns'
 * normalizeMessageCategoryForUI('LOSSRUNS') // returns 'LossRuns'
 * normalizeMessageCategoryForUI('Claimsops') // returns 'ClaimsOps'
 * normalizeMessageCategoryForUI('Submission') // returns 'Submission'
 * normalizeMessageCategoryForUI('BOR') // returns 'BOR'
 * normalizeMessageCategoryForUI('Other') // returns 'Other'
 * normalizeMessageCategoryForUI(undefined) // returns 'Submission'
 */

export const normalizeMessageCategoryForUI = (msgCategory?: string): string => {
  if (!msgCategory) {
    return 'Submission';
  }

  // Handle known case variations - return camel case for UI display
  const categoryMap: Record<string, string> = {
    Lossruns: 'LossRuns',
    lossruns: 'LossRuns',
    LOSSRUNS: 'LossRuns',
    Claims: 'Claims',
    claims: 'Claims',
    CLAIMS: 'Claims',
    ClaimsOps: 'ClaimsOps',
    Claimsops: 'ClaimsOps',
    claimsops: 'ClaimsOps',
    CLAIMSOPS: 'ClaimsOps',
    CLAIMS_OPS: 'ClaimsOps',
    Submission: 'Submission',
    submission: 'Submission',
    SUBMISSION: 'Submission',
    BOR: 'BOR',
    bor: 'BOR',
    Binders: 'Binders',
    binders: 'Binders',
    BINDERS: 'Binders',
    CRS: 'CRS',
    crs: 'CRS',
    Subjectivity: 'Subjectivity',
    subjectivity: 'Subjectivity',
    SUBJECTIVITY: 'Subjectivity',
    Spam: 'Spam',
    spam: 'Spam',
    SPAM: 'Spam',
    MTE: 'MTE',
    mte: 'MTE',
    Renewal: 'Renewal',
    renewal: 'Renewal',
    RENEWAL: 'Renewal',
    RENEWALS: 'Renewal',
    Cancellation: 'Cancellation',
    cancellation: 'Cancellation',
    CANCELLATION: 'Cancellation',
    CertRequest: 'CertRequest',
    certrequest: 'CertRequest',
    CERT_REQUEST: 'CertRequest',
    API: 'API',
    api: 'API',
    General: 'General',
    general: 'General',
    GENERAL: 'General',
    RiskEng: 'RiskEng',
    riskeng: 'RiskEng',
    RISKENG: 'RiskEng',
    'Risk Engineering': 'RiskEng',
    Finance: 'Finance',
    finance: 'Finance',
    FINANCE: 'Finance',
    Compliance: 'Compliance',
    compliance: 'Compliance',
    COMPLIANCE: 'Compliance',
    Other: 'Other',
    other: 'Other',
    OTHER: 'Other',
  };

  return categoryMap[msgCategory] || msgCategory;
};

/**
 * @deprecated Call {@link normalizeMessageCategoryForUI} or
 * {@link normalizeMessageCategoryForAPI} directly. This alias resolves to the
 * UI function, which the name does not say.
 */
export const normalizeMessageCategory = normalizeMessageCategoryForUI;

/**
 * Formats email type for display
 * @param emailType - Email type to format
 * @returns Formatted email type string
 */
export const formatEmailType = (emailType: EmailType): string => {
  switch (emailType) {
    case 'NEW':
      return 'New Email';
    case 'REPLY':
      return 'Reply';
    case 'FORWARD':
      return 'Forward';
    case 'FOLLOWUP':
      return 'Follow-up';
    default:
      return 'Unknown';
  }
};

/**
 * Colour for the email-type chip in the details panel. The API sends the type
 * upper-case ('NEW', 'REPLY', ...), but older rows are mixed-case, so the type
 * is matched case-insensitively. Unknown types read as dimmed.
 * @param emailType - Email type string, in any casing
 * @returns CSS colour value for the chip
 */
export const getEmailTypeColor = (emailType?: string): string => {
  switch (emailType?.toUpperCase()) {
    case 'NEW':
      return 'var(--mantine-color-blue-6)';
    case 'REPLY':
      return 'var(--mantine-color-blue-8)';
    case 'FOLLOWUP':
      return 'var(--mantine-color-yellow-6)';
    default:
      return 'var(--mantine-color-dimmed)';
  }
};

/**
 * Gets the appropriate icon or label for email type
 * @param emailType - Email type
 * @returns Icon name or label
 */
export const getEmailTypeIcon = (emailType: EmailType): string => {
  switch (emailType) {
    case 'NEW':
      return 'email';
    case 'REPLY':
      return 'reply';
    case 'FORWARD':
      return 'forward';
    case 'FOLLOWUP':
      return 'schedule';
    default:
      return 'email';
  }
};
